import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Badge,
    Progress,
    Table,
    Spinner,
    Alert,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../../api/urls";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import { fetchAccountSummary } from "../../api/requests/account-requests";
import { fetchDashboardStatistics, fetchMonthlyStatistics, fetchRecentPayments, fetchPaymentDistribution, fetchTopAccounts, fetchTimeSeries } from "../../api/requests/dashboard-requests";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
    Area, AreaChart,
    Bar, BarChart,
    CartesianGrid, Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts"
import moment from "moment";

/**
 * Modern Dashboard Header
 */
const DashboardHeader = ({ activePeriod, setActivePeriod, dashboardTitle, isAccountRole,
                             accountSummary,
                             formatCurrency }) => {
    const periods = [
        { key: 'week', label: 'Geçen Hafta', icon: 'bx-calendar-week' },
        { key: 'month', label: 'Geçen Ay', icon: 'bx-calendar' },
        { key: 'year', label: 'Geçen Yıl', icon: 'bx-calendar-alt' },
        { key: 'all', label: 'Tüm Zamanlar', icon: 'bx-history' },
    ];

    return (
        <div className="mb-4" style={{ marginTop: '2.5rem' }}>
            <div className="dashboard-hero p-4 mb-3">
                <div className="d-flex flex-column flex-xxl-row justify-content-between align-items-start gap-3">
                    <div className="flex-shrink-0">
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <div className="rounded-3 d-flex align-items-center justify-content-center"
                                 style={{ width: 36, height: 36, background: 'rgba(255,165,0,0.25)' }}>
                                <i className="bx bx-bar-chart-alt-2 text-warning" style={{ fontSize: '1.2rem' }}></i>
                            </div>
                            <h4 className="fw-bold text-white mb-0">Kontrol Paneli</h4>
                        </div>
                        <p className="mb-0" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem' }}>
                            Tüm Ödeme, İşlem ve Performans Verilerinizi Tek Yerden Kontrol Edin
                        </p>
                    </div>

                    <div className="flex-grow-1 d-flex justify-content-start">
                        {isAccountRole && accountSummary && (
                            <CompactAccountSummary summary={accountSummary} formatCurrency={formatCurrency} />
                        )}
                    </div>

                    <div className="d-flex gap-2 flex-wrap justify-content-end flex-shrink-0 mt-2 mt-xxl-0">
                        {periods.map(({ key, label, icon }) => (
                            <button
                                key={key}
                                className={`btn btn-sm px-3 rounded-pill period-btn ${activePeriod === key ? 'active' : ''}`}
                                style={activePeriod === key
                                    ? { backgroundColor: '#FFA500', borderColor: '#FFA500', color: '#fff' }
                                    : { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.85)' }
                                }
                                onClick={() => setActivePeriod(key)}
                            >
                                <i className={`bx ${icon} me-1`}></i>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const WeeklyStats = ({ dashboardData, formatCurrency }) => {

    const getChangeInfo = (value) => {

        const numericValue = value ? parseFloat(value.replace('%', '')) : 0;

        if (numericValue > 0) {
            return {
                change: value,
                type: "success",
                icon: "bx-trending-up",
            };
        } else if (numericValue < 0) {
            return {
                change: value,
                type: "danger",
                icon: "bx-trending-down",
            };
        } else {
            return {
                change: value,
                type: "warning",
                icon: "bx-trending-flat",
            };
        }
    };

    const iconConfig = {
        "Toplam Başarılı Tutar": { icon: "bx-wallet-alt", bg: "#0A316E" },
        "Toplam Ödeme":          { icon: "bx-credit-card", bg: "#FFA500" },
        "Başarılı Ödeme":        { icon: "bx-check-circle", bg: "#28a745" },
        "Başarısız Ödeme":       { icon: "bx-x-circle", bg: "#dc3545" },
        "Ortalama Tutar":        { icon: "bx-bar-chart-alt", bg: "#6f42c1" },
        "Bugünkü Tutar":         { icon: "bx-calendar-check", bg: "#17a2b8" },
    };

    const statsData = [
        {
            label: "Toplam Başarılı Tutar",
            value: formatCurrency(dashboardData.totalAmount || 0),
            ...getChangeInfo(dashboardData.totalAmountChange)
        },
        {
            label: "Toplam Ödeme",
            value: (dashboardData.totalPayments || 0).toLocaleString('tr-TR'),
            ...getChangeInfo(dashboardData.totalPaymentsChange)
        },
        {
            label: "Başarılı Ödeme",
            value: (dashboardData.successfulPayments || 0).toLocaleString('tr-TR'),
            ...getChangeInfo(dashboardData.successfulPaymentsChange)
        },
        {
            label: "Başarısız Ödeme",
            value: (dashboardData.failedPayments || 0).toLocaleString('tr-TR'),
            // Başarısız ödemeler için trendi tersine çeviriyoruz, artış kötü bir durumdur.
            ...getChangeInfo(dashboardData.failedPaymentsChange)
        },
        {
            label: "Ortalama Tutar",
            value: formatCurrency(dashboardData.avgPaymentAmount || 0),
            ...getChangeInfo(dashboardData.avgPaymentAmountChange)
        },
        {
            label: "Bugünkü Tutar",
            value: formatCurrency(dashboardData.todayAmount || 0),
            // Bugüne ait yüzde değişimini dinamik olarak gösteriyoruz.
            ...getChangeInfo(dashboardData.todayAmountChange)
        }
    ];

    return (
        <Card className="bg-white border-0 shadow-lg h-100 rounded-4 card-top-accent-navy">
            <CardHeader className="bg-white border-0 pb-0 rounded-top-4 pt-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-stats text-muted" style={{ fontSize: '1.1rem' }}></i>
                    <h5 className="mb-0 text-dark fw-semibold">Kontrol Paneli Verileri</h5>
                </div>
            </CardHeader>
            <CardBody className="p-4">
                <Row className="g-3">
                    {statsData.map((item, index) => {
                        const numericChange = item.change ? parseFloat(item.change.replace('%', '')) : 0;
                        let badgeType = item.type;
                        let badgeIcon = item.icon;

                        // Başarısız Ödeme için özel koşul
                        if (item.label === "Başarısız Ödeme") {
                            if (numericChange > 0) {
                                badgeType = "danger";
                                badgeIcon = "bx-trending-up";
                            } else if (numericChange < 0) {
                                badgeType = "success";
                                badgeIcon = "bx-trending-down";
                            } else {
                                badgeType = "warning";
                                badgeIcon = "bx-trending-flat";
                            }
                        }

                        // Bugünkü Tutar için özel koşul
                        if (item.label === "Bugünkü Tutar") {
                            if (numericChange > 0) {
                                badgeType = "success";
                                badgeIcon = "bx-trending-up";
                            } else if (numericChange < 0) {
                                badgeType = "danger";
                                badgeIcon = "bx-trending-down";
                            } else {
                                badgeType = "warning";
                                badgeIcon = "bx-trending-flat";
                            }
                        }

                        const cfg = iconConfig[item.label] || { icon: "bx-circle", bg: "#6c757d" };

                        return (
                            <Col md={6} key={index}>
                                <div className="d-flex align-items-center gap-3 p-3 rounded-4 bg-white dashboard-stat-card"
                                     style={{ border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                    <div className="stat-icon-box" style={{ background: cfg.bg + '18' }}>
                                        <i className={`bx ${cfg.icon}`} style={{ color: cfg.bg, fontSize: '1.4rem' }}></i>
                                    </div>
                                    <div className="flex-grow-1 min-width-0">
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>{item.label}</p>
                                        <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '0.95rem' }}>{item.value}</h6>
                                    </div>
                                    <Badge
                                        className={`bg-${badgeType}-subtle text-${badgeType} border-0 rounded-3 px-2 py-1 flex-shrink-0`}
                                        style={{ fontSize: '0.7rem' }}
                                    >
                                        <i className={`bx ${badgeIcon} me-1`}></i>
                                        {item.change}
                                    </Badge>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </CardBody>
        </Card>
    );
};

const PaymentDistributionCard = ({ paymentStats, formatCurrency }) => {
    // Veriyi PieChart için uygun formata getiriyoruz
    const data = [
        { name: 'Tek Çekim', value: paymentStats.singlePaymentPercentage || 0 },
        { name: 'Taksitli', value: paymentStats.installmentPaymentPercentage || 0 }
    ];

    // Grafik renklerini belirleyin
    const COLORS = ['#0A316E', '#FFA500'];

    return (
        <Card className="bg-white border-0 shadow-lg h-100 rounded-4 card-top-accent-orange">
            <CardHeader className="bg-white border-0 pb-0 rounded-top-4 pt-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-pie-chart text-muted" style={{ fontSize: '1.1rem' }}></i>
                    <h5 className="mb-0 text-dark fw-semibold">Ödeme Dağılımı ve En Yüksek İşlem</h5>
                </div>
            </CardHeader>
            <CardBody className="pt-3 d-flex flex-column align-items-center">

                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            fill="#8884d8"
                            paddingAngle={5}
                            dataKey="value"
                            labelLine={false}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Legend
                            layout="vertical"
                            verticalAlign="middle"
                            align="right"
                            iconType="circle"
                            formatter={(value, entry) => (
                                <span className="text-muted small">
                                    {value} (%{entry.payload.value.toFixed(2)})
                                </span>
                            )}
                        />
                    </PieChart>
                </ResponsiveContainer>

                <div className="w-100 mt-2">
                    <div className="d-flex align-items-center gap-3 p-3 rounded-4"
                         style={{ background: 'linear-gradient(135deg, rgba(10,49,110,0.05) 0%, rgba(255,165,0,0.07) 100%)', border: '1px dashed rgba(255,165,0,0.4)' }}>
                        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                             style={{ width: 44, height: 44, background: 'rgba(255,165,0,0.15)' }}>
                            <i className="bx bx-crown" style={{ fontSize: '1.3rem', color: '#FFA500' }}></i>
                        </div>
                        <div>
                            <p className="text-muted mb-0" style={{ fontSize: '0.72rem' }}>En Yüksek İşlem Tutarı</p>
                            <p className="fw-bold text-dark mb-0" style={{ fontSize: '1.05rem' }}>
                                {formatCurrency(paymentStats.highestTransactionAmount || 0)}
                            </p>
                        </div>
                    </div>
                </div>

            </CardBody>
        </Card>
    );
};

const CustomerList = ({ recentTransactions, getStatusBadge, formatCurrency }) => {
    const getInitials = (name) => {
        if (!name) return "M";
        const parts = name.trim().split(" ");
        return (parts[0][0] + (parts[parts.length - 1]?.[0] || "")).toUpperCase();
    };

    return (
        <Card className="bg-white border-0 shadow-lg h-100 rounded-4 card-top-accent-navy">
            <CardHeader className="bg-white border-0 pb-0 d-flex justify-content-between align-items-center rounded-top-4 pt-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-receipt text-muted" style={{ fontSize: '1.1rem' }}></i>
                    <h5 className="mb-0 text-dark fw-semibold">Son Ödemeler</h5>
                </div>
            </CardHeader>
            <CardBody className="p-0 pt-2">
                {recentTransactions.length ? (
                    <div className="table-responsive">
                        <Table className="align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                            <thead>
                            <tr style={{ background: 'linear-gradient(90deg, rgba(10,49,110,0.06) 0%, rgba(10,49,110,0.02) 100%)' }}>
                                <th className="border-0 ps-4 py-3" style={{ color: '#0A316E', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Ad Soyad</th>
                                <th className="border-0 py-3" style={{ color: '#0A316E', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Şirket</th>
                                <th className="border-0 py-3" style={{ color: '#0A316E', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Tutar</th>
                                <th className="border-0 py-3" style={{ color: '#0A316E', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Tarih</th>
                                <th className="border-0 py-3" style={{ color: '#0A316E', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>İşlem Kodu</th>
                                <th className="border-0 py-3" style={{ color: '#0A316E', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Durum</th>
                            </tr>
                            </thead>
                            <tbody>
                            {recentTransactions.slice(0, 6).map((t, index) => (
                                <tr key={t.paymentId || index} className="dashboard-table-row border-bottom">
                                    <td className="ps-4 py-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                                 style={{ width: 32, height: 32, background: `hsl(${(index * 47 + 210) % 360}, 55%, 38%)`, fontSize: '0.65rem' }}>
                                                {getInitials(t.customerName)}
                                            </div>
                                            <span className="fw-medium text-dark">{t.customerName || "Ömer Bekereci"}</span>
                                        </div>
                                    </td>
                                    <td className="py-3">
                                        <span className="text-muted">{t.companyName || "Yüksel Oto Elektrik"}</span>
                                    </td>
                                    <td className="py-3">
                                        <span className="fw-semibold" style={{ color: '#0A316E' }}>{formatCurrency(t.amount || 8000)}</span>
                                    </td>
                                    <td className="py-3">
                                        <div className="fw-medium text-dark" style={{ fontSize: '0.82rem' }}>
                                            {new Date(t.paymentDate || '2025-08-22').toLocaleDateString("tr-TR")}
                                        </div>
                                        <small className="text-muted">
                                            {new Date(t.paymentDate || '2025-08-22T08:29:00').toLocaleTimeString("tr-TR", {
                                                hour: "2-digit",
                                                minute: "2-digit"
                                            })}
                                        </small>
                                    </td>
                                    <td className="py-3">
                                        <code className="small px-2 py-1 rounded-2"
                                              style={{ background: 'rgba(10,49,110,0.06)', color: '#0A316E', fontSize: '0.72rem' }}>
                                            {t.merchantOid || "175585138233571"}
                                        </code>
                                    </td>
                                    <td className="py-3">{getStatusBadge(t.status || "SUCCESS")}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                             style={{ width: 64, height: 64, background: 'rgba(10,49,110,0.07)' }}>
                            <i className="bx bx-receipt" style={{ fontSize: '1.8rem', color: '#0A316E' }}></i>
                        </div>
                        <p className="text-muted mb-0">Henüz işlem bulunmuyor</p>
                    </div>
                )}
            </CardBody>
            <div className="d-flex justify-content-end p-3 pt-2">
                <Link to="/payment-history"
                      className="btn btn-sm rounded-pill px-3 fw-medium"
                      style={{ background: 'rgba(10,49,110,0.07)', color: '#0A316E', border: 'none', fontSize: '0.8rem' }}>
                    Tümünü Göster <i className="bx bx-chevron-right"></i>
                </Link>
            </div>
        </Card>
    );
};
/**
 * Enhanced All Transactions Chart with Better Visual Balance
 */
const AllTransactionsChart = ({ topAccounts }) => {
    const topCustomersData = topAccounts || [];
    const totalPaymentsCount = topCustomersData.reduce((sum, item) => sum + (item.paymentCount || 0), 0);

    const getChartTitle = () => {
        if (topCustomersData.some(item => item.accountName !== null)) {
            return "En Çok Ödeme Yapan 5 Cari/Bayi";
        }
        if (topCustomersData.some(item => item.companyName !== null)) {
            return "En Çok Ödeme Yapan 5 Firma";
        }
        return "En Çok Ödeme Yapanlar";
    };

    const formatCurrency = (amount) => {
        return `₺${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount / 1000)}k`;
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="custom-tooltip bg-white p-3 rounded shadow-sm border">
                    <p className="fw-bold mb-0">{data.accountName || data.companyName}</p>
                    <p className="text-muted small mb-0">
                        Toplam Tutar: <span className="fw-bold text-dark">{`₺${data.totalPaymentAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                    </p>
                    <p className="text-muted small mb-0">
                        İşlem Sayısı: <span className="fw-bold text-dark">{data.paymentCount}</span>
                    </p>
                </div>
            );
        }
        return null;
    };

    const chartData = topCustomersData.map(item => ({
        ...item,
        displayName: item.accountName || item.companyName
    }));
    const formatDisplayName = (name) => {
        if (!name) return '-';
        return name.length > 10 ? name.slice(0, 12) + "..." : name;
    };

    return (
        <Card className="bg-white border-0 shadow-lg h-100 rounded-4 card-top-accent-navy">
            <CardHeader className="bg-white border-0 pb-0 rounded-top-4 pt-4">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <i className="bx bx-trophy text-muted" style={{ fontSize: '1.1rem' }}></i>
                        <h5 className="mb-0 text-dark fw-semibold">{getChartTitle()}</h5>
                    </div>
                    {totalPaymentsCount > 0 && (
                        <span className="rounded-pill px-2 py-1 fw-semibold"
                              style={{ background: 'rgba(10,49,110,0.08)', color: '#0A316E', fontSize: '0.72rem' }}>
                            {totalPaymentsCount} işlem
                        </span>
                    )}
                </div>
            </CardHeader>
            <CardBody className="py-3 d-flex flex-column align-items-center">
                {topCustomersData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={chartData}>
                            <defs>
                                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0A316E" stopOpacity={1} />
                                    <stop offset="100%" stopColor="#221C5D" stopOpacity={0.7} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis
                                dataKey="displayName"
                                angle={-45}
                                textAnchor="end"
                                height={70}
                                tick={{ fontSize: 11, fill: '#6c757d' }}
                                interval={0}
                                tickFormatter={formatDisplayName}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tickFormatter={formatCurrency}
                                tick={{ fontSize: 11, fill: '#6c757d' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(10,49,110,0.04)' }} />
                            <Bar dataKey="totalPaymentAmount" fill="url(#barGradient)" radius={[8, 8, 0, 0]} barSize={28} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="text-center py-5">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                             style={{ width: 64, height: 64, background: 'rgba(10,49,110,0.07)' }}>
                            <i className="bx bx-bar-chart-alt-2" style={{ fontSize: '1.8rem', color: '#0A316E' }}></i>
                        </div>
                        <p className="text-muted mb-0">Bu dönemde ödeme yapan cari/bayi bulunmuyor</p>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};


const SuccessRateChart = ({ timeSeriesData }) => {
    // chartData ve timeUnit artık doğrudan timeSeriesData prop'undan alınıyor
    const chartData = timeSeriesData.series ? [...timeSeriesData.series].reverse() : []; // <-- Burası değişti
    const timeUnit = timeSeriesData.timeUnit || 'Gün';
    const totalAmount = chartData.reduce((sum, data) => sum + (data.totalAmount || 0), 0);
    const formatCurrency = (amount) =>
        new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(amount);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="custom-tooltip bg-white p-2 rounded shadow-sm">
                    <p className="label text-dark fw-bold">{`${data.label}`}</p>
                    <p className="intro text-muted">{`Ödeme: ${formatCurrency(data.totalAmount)}`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <Card className="bg-white border-0 shadow-lg h-100 rounded-4 card-top-accent-orange">
            <CardHeader className="bg-white border-0 pb-0 d-flex justify-content-between align-items-center rounded-top-4 pt-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-line-chart text-muted" style={{ fontSize: '1.1rem' }}></i>
                    <h5 className="mb-0 text-dark fw-semibold">Zaman Serisi Analizi ({timeUnit} Bazlı)</h5>
                </div>
                <span className="rounded-pill px-2 py-1 fw-semibold"
                      style={{ background: 'rgba(255,165,0,0.12)', color: '#e69500', fontSize: '0.78rem' }}>
                    {formatCurrency(totalAmount)}
                </span>
            </CardHeader>
            <CardBody className="py-3">
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <defs>
                                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#FFA500" stopOpacity={0.25} />
                                    <stop offset="95%" stopColor="#FFA500" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis
                                dataKey="label"
                                axisLine={false}
                                tickLine={false}
                                fontSize={11}
                                stroke="#6c757d"
                                padding={{ left: 20, right: 20 }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                fontSize={11}
                                stroke="#6c757d"
                                tickFormatter={(value) => `₺${(value / 1000).toFixed(1)}k`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="totalAmount"
                                stroke="#FFA500"
                                strokeWidth={2.5}
                                fill="url(#areaGradient)"
                                dot={{ stroke: '#FFA500', strokeWidth: 2, r: 3, fill: '#fff' }}
                                activeDot={{ stroke: '#FFA500', strokeWidth: 3, r: 5, fill: '#fff' }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="text-center py-5">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                             style={{ width: 64, height: 64, background: 'rgba(255,165,0,0.1)' }}>
                            <i className="bx bx-line-chart" style={{ fontSize: '1.8rem', color: '#FFA500' }}></i>
                        </div>
                        <p className="text-muted mb-0">Bu dönemde gösterilecek veri bulunmuyor</p>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};


const CompactAccountSummary = ({ summary, formatCurrency }) => {
    if (!summary) return null;

    // HATA DÜZELTME: getBalanceColor buraya taşındı
    const getBalanceColor = (balance) => {
        if (balance == null || balance === 0) return "dark";
        if (balance > 0) return "success"; // Alacak (pozitif) yeşil
        return "danger"; // Borç (negatif) kırmızı
    };

    let calculatedTotalDebtBrut = 0;
    if (summary.currentBalance != null && summary.totalReceiptAmount != null) {
        // Net Borcun mutlak değeri + Toplam Tahsilat = Brüt Borç
        const netDebtMagnitude = Math.abs(summary.currentBalance);
        calculatedTotalDebtBrut = netDebtMagnitude + summary.totalReceiptAmount;
    }


    // Para birimi farkında formatlama (currentBalance / TRY alanları geriye dönük uyumlu kalır)
    const fmtCode = (amount, code = "TRY") =>
        new Intl.NumberFormat("tr-TR", { style: "currency", currency: code }).format(Number(amount || 0));

    const netBalanceLabel = (summary?.currentBalance >= 0) ? "Net Bakiye (Alacak)" : "Net Bakiye (Borç)";
    const netBalanceIcon = summary?.currentBalance >= 0 ? "bx-check-circle" : "bx-x-circle";
    const netBalanceValue = summary?.currentBalance || 0;

    const data = [
        {
            title: netBalanceLabel,
            value: netBalanceValue,
            code: "TRY",
            color: getBalanceColor(netBalanceValue),
            icon: netBalanceIcon,
        },
        {
            title: "Toplam Borç (Brüt)",
            value: calculatedTotalDebtBrut,
            code: "TRY",
            color: "danger",
            icon: "bx-receipt",
        },
        {
            title: "Toplam Tahsilat",
            value: summary.totalReceiptAmount,
            code: "TRY",
            color: "success",
            icon: "bx-trending-up",
        },
    ];

    // TRY dışı para birimlerinde bakiye varsa net bakiye kartı olarak ekle
    (summary?.balances || [])
        .filter((b) => b.currency !== "TRY" && Number(b.currentBalance || 0) !== 0)
        .forEach((b) => {
            const val = Number(b.currentBalance || 0);
            data.push({
                title: `Net Bakiye (${b.currency})`,
                value: val,
                code: b.currency,
                color: getBalanceColor(val),
                icon: val >= 0 ? "bx-check-circle" : "bx-x-circle",
            });
        });

    return (
        <div className="d-flex gap-2 flex-wrap-reverse flex-md-nowrap align-items-center me-3">
            {data.map((item, index) => (
                <div
                    key={index}
                    // DEĞİŞİKLİK: bg-light yerine bg-white kullanıldı
                    className="p-2 rounded-3 bg-white shadow-sm border"
                    style={{
                        flex: '1 1 auto', // Esnekliği ayarla
                        minWidth: '100px',
                        maxWidth: '180px',
                        fontSize: '0.8rem',
                        lineHeight: '1.2'
                    }}
                >
                    <p className="text-muted small mb-0 fw-medium text-truncate" style={{ fontSize: '0.7rem' }}>
                        {item.title}
                    </p>
                    <div className={`d-flex align-items-center gap-1`}>
                        <i className={`bx ${item.icon} text-${item.color}`} style={{ fontSize: '0.9rem' }}></i>
                        <span className="text-dark fw-bold" style={{ fontSize: '0.9rem' }}>
                            {fmtCode(item.value, item.code)}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
};

const Dashboard = ({ isSessionModalActive }) => {
    useEffect(() => {
        document.title = "Kontrol Paneli | Tahsilat360";
    }, []);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        totalPayments: 0,
        totalAmount: 0,
        successRate: 87.9,
        pendingPayments: 0,
        successfulPayments: 0,
        failedPayments: 0,
        avgPaymentAmount: 0,
        todayAmount: 0,
        todayPayments: 0,
    });
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [monthlyStats, setMonthlyStats] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [activePeriod, setActivePeriod] = useState('week');
    const isFirstLoad = useRef(true);
    const [topAccounts, setTopAccounts] = useState([]);
    const [timeSeriesData, setTimeSeriesData] = useState([]);

    const [currentUser, setCurrentUser] = useState(null);
    const [accountSummary, setAccountSummary] = useState(null);

    // YARDIMCI ROL KONTROLÜ
    const isAccountRole = currentUser?.accountId && (
        currentUser?.roles?.includes("ROLE_ACCOUNT_ADMIN") ||
        currentUser?.roles?.includes("ROLE_ACCOUNT_ACCOUNTANT")
    );

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const response = await fetchAuthMe();

                if (response.ok) {
                    const { data } = await response.json();
                    setCurrentUser(data);
                }
            } catch (error) {
                console.error("User info error:", error);
            }
        };

        fetchUserInfo();
    }, []);

    useEffect(() => {
        if (!currentUser || !isAccountRole || !currentUser.accountId) {
            setAccountSummary(null); // Rol değişirse veya veri yoksa sıfırla
            return;
        }

        const fetchAccountSummary = async () => {
            try {
                const accountId = currentUser.accountId;

                const response = await fetchAccountSummary(accountId);

                if (!response.ok) {
                    throw new Error('Cari özet bilgileri alınırken hata oluştu');
                }

                const result = await response.json();
                if (result.statusCode === 200 && result.data) {
                    setAccountSummary(result.data);
                } else {
                    setAccountSummary(null);
                }

            } catch (err) {
                console.error('Account Summary fetch error:', err);
                // Hata durumunda da boş veya null set edilebilir.
                setAccountSummary(null);
            }
        };

        fetchAccountSummary();
    }, [currentUser, isAccountRole]);


    const getDashboardTitle = () => {
        switch (activePeriod) {
            case 'week': return 'Bu hafta';
            case 'month': return 'Bu ay';
            case 'year': return 'Bu yıl';
            case 'all': return 'Tüm Zamanlar';
            default: return 'Bu hafta';
        }
    };


    const [paymentStats, setPaymentStats] = useState({
        installmentPaymentPercentage: 0,
        singlePaymentPercentage: 0,
        highestTransactionAmount: 0
    });
    useEffect(() => {
        if (isSessionModalActive) {
            return;
        }

        const fetchDashboardData = async (isInitialLoad = false) => {
            try {
                if (isInitialLoad) {
                    setLoading(true);
                } else {
                    setRefreshing(true);
                }
                setError(null);

                // Tarih aralığını belirle
                let startDate, endDate;
                const now = moment();

                switch (activePeriod) {
                    case 'week':
                        startDate = now.clone().subtract(6, 'days').format('YYYY-MM-DD');
                        break;
                    case 'month':
                        startDate = now.clone().subtract(29, 'days').format('YYYY-MM-DD');
                        break;
                    case 'year':
                        startDate = now.clone().subtract(364, 'days').format('YYYY-MM-DD');
                        break;
                    case 'all':
                        startDate = '1999-01-01';
                        endDate = now.format('YYYY-MM-DD');
                        break;
                    default:
                        // Varsayılan olarak 'week' mantığı
                        startDate = now.clone().subtract(6, 'days').format('YYYY-MM-DD');
                        endDate = now.format('YYYY-MM-DD');
                        break;
                }


                const [dashboardResponse, monthlyResponse, recentResponse, paymentDistributionResponse,topAccountsResponse, timeSeriesResponse, ] = await Promise.all([
                    fetchDashboardStatistics(null, startDate, endDate),
                    fetchMonthlyStatistics(null),
                    fetchRecentPayments(null),
                    fetchPaymentDistribution(null, startDate, endDate),
                    fetchTopAccounts(null, startDate, endDate),
                    fetchTimeSeries(null, activePeriod)
                ]);

                if (!dashboardResponse.ok || !monthlyResponse.ok || !recentResponse.ok) {
                    if (dashboardResponse.status === 401 || monthlyResponse.status === 401 || recentResponse.status === 401) {
                        throw new Error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                    }
                    throw new Error('Veri alınırken hata oluştu');
                }

                const dashboardResult = await dashboardResponse.json();
                const monthlyResult = await monthlyResponse.json();
                const recentResult = await recentResponse.json();
                const paymentDistributionResult = await paymentDistributionResponse.json();
                const topAccountsResult = await topAccountsResponse.json();
                const timeSeriesResult = await timeSeriesResponse.json();


                if (dashboardResult.statusCode === 200 && dashboardResult.data) {
                    setDashboardData(dashboardResult.data);
                }

                if (monthlyResult.statusCode === 200 && monthlyResult.data) {
                    setMonthlyStats(monthlyResult.data);
                }
                if (timeSeriesResult.statusCode === 200 && timeSeriesResult.data) {
                    setTimeSeriesData(timeSeriesResult.data); // <-- YENİ STATE'İ GÜNCELLE
                }

                if (recentResult.statusCode === 200 && recentResult.data) {
                    setRecentTransactions(recentResult.data);
                }
                if (paymentDistributionResult.statusCode === 200 && paymentDistributionResult.data) {
                    setPaymentStats(paymentDistributionResult.data);
                }
                if (topAccountsResult.statusCode === 200 && topAccountsResult.data) {
                    setTopAccounts(topAccountsResult.data);
                } else {
                    setTopAccounts([]); // Hata veya boş veri durumunda boş dizi ata
                }

            } catch (err) {
                console.error('Dashboard data fetch error:', err);
                setError(err.message);
            } finally {
                if (isInitialLoad) {
                    setLoading(false);
                } else {
                    setRefreshing(false);
                }
            }
        };

        const initialLoad = isFirstLoad.current;
        isFirstLoad.current = false;
        fetchDashboardData(initialLoad);
        const interval = setInterval(() => fetchDashboardData(false), 30000); // 30 saniyede bir yenile
        return () => clearInterval(interval);
    }, [navigate, isSessionModalActive, activePeriod]);


    useEffect(() => {
        if (localStorage.getItem("dashboardNeedsRefresh") === "true") {
            localStorage.removeItem("dashboardNeedsRefresh");
            window.location.reload();
        }
    }, []);

    const getStatusBadge = (status) => {
        switch (status) {
            case "COMPLETED":
            case "SUCCESS":
                return <Badge className="bg-success text-white fw-semibold rounded-pill px-2 py-1 shadow-sm">Başarılı</Badge>;
            case "PENDING":
            case "WAITING":
                return <Badge className="bg-warning text-white fw-semibold rounded-pill px-2 py-1 shadow-sm">Beklemede</Badge>;
            case "FAILED":
            case "CANCELLED":
                return <Badge className="bg-danger text-white fw-semibold rounded-pill px-2 py-1 shadow-sm">Başarısız</Badge>;
            // İade durumları eşlemede yoktu; default'a düşüp "Bilinmiyor" görünüyorlardı.
            // Etiket ve renkler Paymenthistoryconstants.js'teki PAYMENT_STATUS ile aynı.
            case "PARTIALLY_REFUNDED":
                return <Badge className="bg-info text-white fw-semibold rounded-pill px-2 py-1 shadow-sm">Kısmi İade</Badge>;
            case "REFUNDED":
                return <Badge className="bg-secondary text-white fw-semibold rounded-pill px-2 py-1 shadow-sm">İade Edildi</Badge>;
            default:
                return <Badge className="bg-secondary text-white fw-semibold rounded-pill px-2 py-1 shadow-sm">Bilinmiyor</Badge>;
        }
    };

    const formatCurrency = (amount) =>
        new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(amount);

    if (loading) {
        return (
            <Container fluid className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                         style={{ width: 72, height: 72, background: 'linear-gradient(135deg, #0A316E18, #FFA50018)' }}>
                        <Spinner size="md" style={{ color: '#0A316E', width: '2rem', height: '2rem' }} />
                    </div>
                    <h6 className="mt-1 fw-semibold" style={{ color: '#0A316E' }}>Veriler yükleniyor...</h6>
                    <p className="text-muted small mb-0">Lütfen bekleyin</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="mt-4">
                <Alert color="danger" className="border-0 shadow-lg">
                    <i className="bx bx-error-circle me-2"></i>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className={`py-5 px-4 mb-5 dashboard-container${refreshing ? ' is-loading' : ''}`} style={{ background: '#f4f6f9', minHeight: '100vh' }}>
            <DashboardHeader
                activePeriod={activePeriod}
                setActivePeriod={setActivePeriod}
                dashboardTitle={getDashboardTitle()}
                isAccountRole={isAccountRole}
                accountSummary={accountSummary}
                formatCurrency={formatCurrency}
            />
            <Row className="g-4" data-tour="dashboard-stats">

                <Col lg={8}>
                    <WeeklyStats dashboardData={dashboardData} formatCurrency={formatCurrency} />
                </Col>

                <Col lg={4}>
                    <PaymentDistributionCard
                        paymentStats={paymentStats}
                        formatCurrency={formatCurrency}
                    />
                </Col>
            </Row>

            <Row className="g-4 mt-0">
                <Col lg={7}>
                    <CustomerList
                        recentTransactions={recentTransactions}
                        getStatusBadge={getStatusBadge}
                        formatCurrency={formatCurrency}
                    />
                </Col>

                <Col lg={5}>
                    <Row className="g-4">
                        <Col lg={12}>
                            <AllTransactionsChart topAccounts={topAccounts} />

                        </Col>
                        <Col lg={12}>
                            <SuccessRateChart timeSeriesData={timeSeriesData} />

                        </Col>
                    </Row>
                </Col>
            </Row>
        </Container>
    );
};

Dashboard.propTypes = {
    isSessionModalActive: PropTypes.bool
};

const mapStateToProps = state => ({
    isSessionModalActive: state.Login.isSessionModalActive
});

export default connect(mapStateToProps)(Dashboard);